// MyFolio.com.au MUI theme (created under v5; still valid on MUI v7).
import { createTheme, alpha } from '@mui/material/styles';

const primaryMainColor = '#2e928a';

// Application theme configuration
export const muiV5Theme = createTheme({
  palette: {
    primary: {
      light: '#ccdfcc',
      main: primaryMainColor,
      dark: '#0c555c',
      // White on teal — used by contained primary buttons / filled accents.
      contrastText: '#ffffff',
    },
    secondary: {
      light: '#819ca9',
      main: '#546e7a',
      dark: '#29434e',
      contrastText: '#fff',
    },
    background: {
      default: '#f7f8f8',
      paper: '#ffffff',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: [
      '"Source Sans 3"',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    // h1–h3 stay on the body sans (Source Sans 3). Do not introduce a
    // second display face — serif looked wrong on product UI chrome.
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          marginTop: '8px',
          marginBottom: '8px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    // Rounded accordion shells must clip filled summary/actions; without
    // overflow:hidden those backgrounds paint square over the radius
    // (reported twice — SPA calculator + portfolio/ledger shells).
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => {
          return {
            borderRadius: `${theme.shape.borderRadius}px !important`,
            overflow: 'hidden',
            '&:before': {
              display: 'none',
            },
          };
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // MUI's default; keep explicit so custom styled Cards don't drop it.
          overflow: 'hidden',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        // Brand chrome — teal bar with light nav text/icons.
        color: 'primary',
        elevation: 0,
      },
    },
    // ListItem no longer accepts `button` (MUI v5+); clickable rows use
    // ListItemButton. Keep selected styling on the button, and kill the
    // browser's purple/blue visited-link colors when it renders as <a>.
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: 'inherit',
          textDecoration: 'none',
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: alpha(primaryMainColor, 0.14),
            color: 'inherit',
            '&:hover': {
              backgroundColor: alpha(primaryMainColor, 0.22),
            },
          },
          '&:hover': {
            textDecoration: 'none',
          },
        },
      },
    },
  },
});

export default muiV5Theme;
